// PullToRefresh.js
//
// Mounted once at the app root (index.js), wrapping everything, so every screen gets it —
// most useful on mobile, where public/manifest.json's display:"standalone" means an installed
// PWA has no browser chrome at all (no address bar, no native pull-to-refresh) once launched
// from the home screen. Deliberately plain touch events, no gesture library — matches
// SignaturePad.js's existing manual-touch-handling convention, the only other place in this
// codebase reading raw touch events.
import React, { useRef, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { COLORS, SHADOW, SAFE_INSET } from './theme';

const PULL_THRESHOLD = 70; // px of (resistance-adjusted) downward drag before release triggers a refresh
const MAX_PULL = 90; // px — caps the indicator's travel for a natural rubber-band feel

// Only starts tracking a pull if the touch begins at the very top of whatever's actually
// scrollable under the finger — walks up from the touch target to the nearest element that can
// scroll at all, and checks THAT element's own scrollTop, not just the page's. Without this, a
// downward drag while scrolled to the top of a modal's own internal content (e.g.
// WorkOrderDetail.js's tab body) would incorrectly trigger a page-level refresh instead of just
//... not scrolling any further, since the underlying page itself is always at scrollY 0 while
// any position:fixed modal is open.
const isAtTopOfNearestScrollable = (target) => {
  let el = target;
  while (el && el !== document.body && el !== document.documentElement) {
    const style = window.getComputedStyle(el);
    const scrollable = /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight;
    if (scrollable) return el.scrollTop <= 0;
    el = el.parentElement;
  }
  return window.scrollY <= 0;
};

// Applies a service-worker update if one turns out to be available, otherwise just reloads — a
// plain reload alone can still leave a real update sitting WAITING rather than actually applied
// (see UpdateAvailableBanner.js's own header comment for exactly why), so this makes "pull to
// refresh" also double as "get me the actual latest deployed version," not just re-render
// whatever's already cached. Self-contained rather than sharing UpdateAvailableBanner.js's own
// handler, since that one already has a registration handed to it via the sw-update-available
// event; this one has to go look for one itself.
const refreshApp = async () => {
  if (!('serviceWorker' in navigator)) {
    window.location.reload();
    return;
  }
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
      if (registration.waiting) {
        let reloaded = false;
        const reloadOnce = () => { if (!reloaded) { reloaded = true; window.location.reload(); } };
        navigator.serviceWorker.addEventListener('controllerchange', reloadOnce);
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        setTimeout(reloadOnce, 1500); // in case controllerchange never fires
        return;
      }
    }
  } catch (error) {
    // fall through to a plain reload below
  }
  window.location.reload();
};

function PullToRefresh({ children }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(null);
  const trackingRef = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (refreshing || e.touches.length !== 1) return;
    if (!isAtTopOfNearestScrollable(e.target)) return;
    startYRef.current = e.touches[0].clientY;
    trackingRef.current = true;
  }, [refreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!trackingRef.current || startYRef.current == null) return;
    const delta = e.touches[0].clientY - startYRef.current;
    if (delta <= 0) {
      setPullDistance(0);
      return;
    }
    // Resistance curve — the drag needs to travel roughly 2x MAX_PULL in real finger movement to
    // reach the visual cap, the same "gets harder to pull the further you go" feel as native
    // iOS/Android pull-to-refresh.
    setPullDistance(Math.min(delta * 0.5, MAX_PULL));
    e.preventDefault();
  }, []);

  const handleTouchEnd = useCallback(() => {
    trackingRef.current = false;
    startYRef.current = null;
    if (pullDistance >= PULL_THRESHOLD) {
      setRefreshing(true);
      refreshApp();
    } else {
      setPullDistance(0);
    }
  }, [pullDistance]);

  const indicatorVisible = pullDistance > 0 || refreshing;
  const indicatorY = refreshing ? 16 : pullDistance - 40;

  return (
    <div onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {indicatorVisible && (
        <div
          style={{
            position: 'fixed', top: SAFE_INSET.top, left: '50%', zIndex: 10001,
            transform: `translate(-50%, ${indicatorY}px)`,
            transition: trackingRef.current ? 'none' : 'transform 0.2s ease',
            width: '36px', height: '36px', borderRadius: '50%', backgroundColor: COLORS.white,
            boxShadow: SHADOW.raised,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <RefreshCw
            size={17}
            color={COLORS.primary}
            strokeWidth={2.5}
            style={{
              transform: refreshing ? 'none' : `rotate(${pullDistance * 3}deg)`,
              animation: refreshing ? 'ptr-spin 0.8s linear infinite' : 'none'
            }}
          />
        </div>
      )}
      <style>{`@keyframes ptr-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      {children}
    </div>
  );
}

export default PullToRefresh;
